/**
 * Panel data model. Since C2 these are the contract's panel view model types
 * (@twin/contracts, specs/c2-kontratlar.md 2.10 and 2.12): the API presenter builds them,
 * the panel only reads them. Money is integer micro-USD (1 USD = 1_000_000) and every
 * instant is epoch milliseconds, so the panel never parses dates on the hot path.
 *
 * The aliases below keep the panel's own names (AgentId, RequestType, ...) for the parts
 * of the contract types the components use.
 */

import type {
  Agent,
  Handoff,
  PanelAgentId,
  Proposal,
  RejectReasonCode,
  RequestRejectCode,
  Run,
  Snapshot,
  TwinRequest,
} from '@twin/contracts';

export type {
  Agent,
  ApprovalTicket,
  ApproveInput,
  CouponProposal,
  CustomerReplyProposal,
  DoneItem,
  DoneStatus,
  ExecutorAction,
  GenericProposal,
  Handoff,
  HoldTicket,
  KeyValue,
  ListingTitleProposal,
  LiveFeedItem,
  PanelSystem,
  Proposal,
  ReadinessFact,
  ReadinessItem,
  ReadinessLevel,
  RejectReasonCode,
  RequestRejectCode,
  RequestSourceType,
  Run,
  Snapshot,
  Tier,
  TwinRequest,
  VerifierVerdict,
} from '@twin/contracts';

/** Epoch milliseconds. */
export type Instant = number;

/** Integer micro US dollars. */
export type MicroUsd = number;

/** Panel agent id (PANEL_AGENT_IDS); HTTP paths take the config id instead. */
export type AgentId = PanelAgentId;
export type AgentShape = Agent['shape'];

/** Encoded by fill style plus a word: solid, outline, dashed, grey fill, red fill. */
export type AgentState = Agent['state'];
export type AgentBudget = Agent['budget'];
export type AgentRunProgress = NonNullable<Agent['progress']>;

export type TierTopic = Proposal['topic'];
export type VerifierResult = Proposal['verifier'];
export type CheckResult = Check['result'];
/** One cell of the CheckStrip. Only verifiable checks carry a result glyph. */
export type Check = Proposal['checks'][number];
export type SourceRef = Proposal['sources'][number];
export type Reasoning = Proposal['reasoning'][number];
export type ProposalKind = Proposal['kind'];

export type RequestType = TwinRequest['type'];
export type RequestStatus = TwinRequest['status'];
export type QuestionRequest = Extract<TwinRequest, { type: 'question' }>;
export type DesignRequest = Extract<TwinRequest, { type: 'design' }>;
export type PhotoRequest = Extract<TwinRequest, { type: 'photo' }>;

export type RunStep = Run['steps'][number];
export type Artifact = NonNullable<RunStep['artifact']>;
export type ArtifactKind = Artifact['kind'];
export type HandoffState = Handoff['state'];

export type StoreSummary = Snapshot['store'];
/** Bugün's business tiles (audit 25 Oct 2026). */
export type Pulse = NonNullable<Snapshot['pulse']>;
export type Sale = StoreSummary['sales'][number];
export type ScheduleItem = Snapshot['schedule'][number];
export type RejectedItem = Snapshot['rejected'][number];

/** Reasons on the wire are codes; the panel shows these labels (2.10 row 5). */
export const REJECT_REASON_LABELS: Readonly<Record<RejectReasonCode, string>> = Object.freeze({
  wrong_info: 'Yanlış bilgi',
  tone: 'Ton',
  unnecessary: 'Gereksiz',
  timing: 'Zamanlama',
  policy: 'Politika',
  other: 'Diğer',
  already_done: 'Zaten yaptım',
});

export const REQUEST_REJECT_REASON_LABELS: Readonly<Record<RequestRejectCode, string>> = Object.freeze({
  not_needed: 'Gerek yok',
  not_now: 'Şimdi değil',
  wrong_info: 'Yanlış bilgi',
  other: 'Diğer',
});
